import { useGetUserQuery } from "features/auth/auth";
import Layout from "../components/Layout";
import {
  CircularProgress,
  Container,
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { ArrowBack, Home } from "@mui/icons-material";
import FileList from "../components/fileStorage/FileList";
import type { TimeDisplayMode } from "../components/fileStorage/FileItem";
import FileSearch from "../components/fileStorage/FileSearch";
import FolderTypeFilter from "../components/fileStorage/FolderTypeFilter";
import { useState, useEffect, useCallback } from "react";
import { FileItem, FolderType } from "../types/interfaces/filesStorage";
import { useGetFileSystemQuery } from "features/fileSystem/filesStorage";
import { useDownloadManager } from "../hooks/useDownloadManager";
import {
  paginationStorage,
  folderMappingsStorage,
} from "../utils/localStorage";
import { storagePaginationValues } from "../utils/constants";
import { useSearchParams, useNavigate } from "react-router-dom";

export default function HomePage() {
  const { data: user, isLoading: userLoading } = useGetUserQuery(undefined, {});
  const isCalibrateAdmin = user?.is_calibrate_admin === true;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialState = useCallback(() => {
    const prefix = searchParams.get("prefix") || "";
    const page = parseInt(searchParams.get("page") || "1");
    const search = searchParams.get("search") || null;
    const folderType = (searchParams.get("folder_type") as FolderType) || "all";
    const urlSortField = searchParams.get("sort_field");
    const urlSortOrder = searchParams.get("sort_order");
    const hasSortInUrl = urlSortField && urlSortOrder;
    const sortBy =
      prefix === ""
        ? hasSortInUrl && urlSortField === "name"
          ? `${urlSortField}_${urlSortOrder}`
          : "name_asc"
        : `${urlSortField || "date"}_${urlSortOrder || "desc"}`;
    const timeDisplay =
      (searchParams.get("time_display") as TimeDisplayMode) || "local";

    return { prefix, page, search, folderType, sortBy, timeDisplay };
  }, [searchParams]);

  const [currentLimit, setCurrentLimit] = useState(() => {
    const savedLimit = paginationStorage.getPaginationValue();
    const validLimit = storagePaginationValues.includes(savedLimit)
      ? savedLimit
      : 25;
    return validLimit;
  });

  const [currentPrefix, setCurrentPrefix] = useState(getInitialState().prefix);
  const [currentSearch, setCurrentSearch] = useState<string | null>(
    getInitialState().search,
  );
  const [currentFolderType, setCurrentFolderType] = useState<FolderType>(
    getInitialState().folderType,
  );
  const [currentContinuationToken, setCurrentContinuationToken] = useState<
    string | null
  >(null);
  const [currentPage, setCurrentPage] = useState(getInitialState().page);
  const [pageTokens, setPageTokens] = useState<string[]>([]);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState(getInitialState().sortBy);
  const [timeDisplayMode, setTimeDisplayMode] = useState<TimeDisplayMode>(
    getInitialState().timeDisplay,
  );
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  const downloadManager = useDownloadManager();

  const updateURL = useCallback(
    (newState: {
      prefix?: string;
      page?: number;
      search?: string | null;
      folderType?: FolderType;
      sortBy?: string;
      timeDisplay?: TimeDisplayMode;
    }) => {
      const currentState = getInitialState();
      const updatedState = { ...currentState, ...newState };

      const params = new URLSearchParams();
      if (updatedState.prefix) params.set("prefix", updatedState.prefix);
      if (updatedState.page > 1)
        params.set("page", updatedState.page.toString());
      if (updatedState.search) params.set("search", updatedState.search);
      if (
        updatedState.folderType &&
        updatedState.folderType !== "all"
      )
        params.set("folder_type", updatedState.folderType);
      const sortField = updatedState.sortBy?.split("_")[0];
      const sortOrder = updatedState.sortBy?.split("_")[1];
      const isRootNameSort =
        !updatedState.prefix &&
        (updatedState.sortBy === "name_asc" || updatedState.sortBy === "name_desc");
      if (
        updatedState.sortBy &&
        (updatedState.prefix || isRootNameSort)
      ) {
        params.set("sort_field", sortField!);
        params.set("sort_order", sortOrder!);
      }
      if (updatedState.timeDisplay && updatedState.timeDisplay !== "local")
        params.set("time_display", updatedState.timeDisplay);

      setSearchParams(params);
    },
    [getInitialState, setSearchParams],
  );

  const sortFromUrl = currentPrefix
    ? {
        field: (searchParams.get("sort_field") || "date") as
          | "name"
          | "date"
          | "size",
        order: (searchParams.get("sort_order") || "desc") as "asc" | "desc",
      }
    : null;
  const sortField = sortFromUrl
    ? sortFromUrl.field
    : (sortBy.split("_")[0] as "name" | "date" | "size");
  const sortOrder = sortFromUrl
    ? sortFromUrl.order
    : (sortBy.split("_")[1] as "asc" | "desc");
  const displaySortBy = sortFromUrl
    ? `${sortFromUrl.field}_${sortFromUrl.order}`
    : sortBy;

  const apiSortField =
    !isCalibrateAdmin && sortField === "date" ? "name" : sortField;
  const apiSortOrder =
    !isCalibrateAdmin && sortField === "date" ? "asc" : sortOrder;

  const {
    data: fileSystemData,
    isLoading: fileSystemLoading,
    error: fileSystemError,
    isFetching: fileSystemFetching,
    refetch: refetchFileSystem,
  } = useGetFileSystemQuery({
    prefix: currentPrefix,
    continuation_token: currentContinuationToken,
    search: currentSearch,
    limit: currentLimit,
    folder_type: currentFolderType,
    page: currentPage,
    sort_field: apiSortField,
    sort_order: apiSortOrder,
  });

  const validSortOptions = isCalibrateAdmin
    ? ["date_desc", "date_asc", "size_desc", "size_asc", "name_asc", "name_desc"]
    : ["size_desc", "size_asc", "name_asc", "name_desc"];

  const sortSelectValue = (() => {
    const fromState = validSortOptions.includes(sortBy) ? sortBy : null;
    const fromUrl =
      !fileSystemData?.has_direct_files &&
      (displaySortBy === "date_desc" ||
        displaySortBy === "date_asc" ||
        displaySortBy === "size_desc" ||
        displaySortBy === "size_asc")
        ? "name_asc"
        : !isCalibrateAdmin &&
            (displaySortBy === "date_desc" || displaySortBy === "date_asc")
          ? "name_asc"
          : validSortOptions.includes(displaySortBy)
            ? displaySortBy
            : "name_asc";
    return fromState ?? fromUrl;
  })();

  useEffect(() => {
    if (fileSystemData?.sync_status !== "syncing") return;
    const interval = setInterval(() => refetchFileSystem(), 3000);
    return () => clearInterval(interval);
  }, [fileSystemData?.sync_status, refetchFileSystem]);

  useEffect(() => {
    if (userLoading) return;
    if (
      !isCalibrateAdmin &&
      (sortField === "date" || sortBy === "date_asc" || sortBy === "date_desc")
    ) {
      setSortBy("name_asc");
      updateURL({ sortBy: "name_asc" });
    }
  }, [userLoading, isCalibrateAdmin, sortField, sortBy, updateURL]);

  const isDbBacked = !!fileSystemData?.total_pages;

  useEffect(() => {
    if (
      fileSystemData?.show_filter !== null &&
      fileSystemData?.show_filter !== undefined
    ) {
      setShowFilter(fileSystemData.show_filter);
    }
  }, [fileSystemData?.show_filter]);

  useEffect(() => {
    const effectiveSort = currentPrefix ? displaySortBy : sortBy;
    const sortRequiresFiles =
      effectiveSort === "date_desc" ||
      effectiveSort === "date_asc" ||
      effectiveSort === "size_desc" ||
      effectiveSort === "size_asc";
    if (
      isDbBacked &&
      fileSystemData?.has_direct_files === false &&
      sortRequiresFiles
    ) {
      setSortBy("name_asc");
      setCurrentPage(1);
      setPageTokens([]);
      updateURL({ page: 1, sortBy: "name_asc" });
    }
  }, [
    isDbBacked,
    fileSystemData?.has_direct_files,
    currentPrefix,
    displaySortBy,
    sortBy,
  ]);

  useEffect(() => {
    if (
      fileSystemData &&
      !fileSystemData.continuation_token &&
      currentPage > 1
    ) {
    }
  }, [fileSystemData?.continuation_token, currentPage]);

  const handleItemClick = (item: FileItem) => {
    if (item.ftype === "folder") {
      const originalItem = fileSystemData?.folders.find(
        (folder) => folder.fsource === item.fsource,
      );
      const fnameToStore = originalItem?.fname || item.fname;

      folderMappingsStorage.clearIrrelevantMappings(item.fsource);
      folderMappingsStorage.setFolderMapping(fnameToStore, item.fsource);

      setCurrentPrefix(item.fsource);
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
      updateURL({ prefix: item.fsource, page: 1, search: null });
    } else {
    }
  };

  const showNotification = (
    message: string,
    severity: "success" | "error" | "info",
  ) => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleDownloadClick = async (item: FileItem) => {
    const originalItem =
      fileSystemData?.folders.find(
        (folder) => folder.fsource === item.fsource,
      ) || item;

    const result = await downloadManager.downloadItem(originalItem);

    if (result.success) {
      showNotification(result.message, "success");
    } else {
      showNotification(result.message, "error");
    }
  };

  const handleLimitChange = (limit: number) => {
    const saved = paginationStorage.setValidPaginationValue(
      limit,
      storagePaginationValues,
    );
    if (!saved) {
    }

    setCurrentLimit(limit);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
    updateURL({ page: 1 }); // Only update page, limit stays in localStorage
  };

  const handleSortChange = (next: string) => {
    setSortBy(next);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
    updateURL({ page: 1, sortBy: next });
  };

  const handlePageChange = (page: number) => {
    if (isDbBacked) {
      if (page < 1 || page > (fileSystemData?.total_pages || 1)) {
        return;
      }
      setCurrentPage(page);
      updateURL({ page });
      return;
    }

    const maxPages = fileSystemData?.continuation_token
      ? pageTokens.length + 1
      : currentPage;

    if (page < 1 || page > maxPages) {
      return;
    }

    if (page === 1) {
      setCurrentContinuationToken(null);
    } else if (page <= pageTokens.length + 1) {
      const tokenIndex = page - 2;
      setCurrentContinuationToken(pageTokens[tokenIndex]);
    } else {
      return;
    }

    setCurrentPage(page);
    updateURL({ page });
  };

  const handleSearch = useCallback(
    (searchTerm: string | null) => {
      setCurrentSearch(searchTerm);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
      updateURL({ search: searchTerm, page: 1 });
    },
    [updateURL],
  );

  const handleFolderTypeChange = useCallback(
    (folderType: FolderType) => {
      setCurrentFolderType(folderType);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
      updateURL({ folderType, page: 1 });
    },
    [updateURL],
  );

  useEffect(() => {
    if (isDbBacked) return;

    if (fileSystemData?.continuation_token && currentPage > 0) {
      setPageTokens((prev) => {
        const newTokens = [...prev];
        if (currentPage > newTokens.length) {
          newTokens.push(fileSystemData.continuation_token!);
        } else {
          newTokens[currentPage - 1] = fileSystemData.continuation_token!;
        }
        return newTokens;
      });
    } else if (!fileSystemData?.continuation_token && currentPage > 0) {
      setPageTokens((prev) => prev.slice(0, currentPage - 1));
    }
  }, [fileSystemData?.continuation_token, currentPage, isDbBacked]);

  useEffect(() => {
    const currentState = getInitialState();

    if (currentState.prefix !== currentPrefix) {
      setCurrentPrefix(currentState.prefix);
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
    }

    if (currentState.page !== currentPage) {
      setCurrentPage(currentState.page);
    }

    if (currentState.search !== currentSearch) {
      setCurrentSearch(currentState.search);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
    }

    if (currentState.folderType !== currentFolderType) {
      setCurrentFolderType(currentState.folderType);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
    }

    if (currentState.sortBy !== sortBy) {
      setSortBy(currentState.sortBy);
      setCurrentPage(1);
      setCurrentContinuationToken(null);
      setPageTokens([]);
    }

    if (currentState.timeDisplay !== timeDisplayMode) {
      setTimeDisplayMode(currentState.timeDisplay);
    }
  }, [
    searchParams,
    getInitialState,
    currentPrefix,
    currentPage,
    currentSearch,
    currentFolderType,
    sortBy,
    timeDisplayMode,
  ]);

  const handleGoBack = () => {
    if (currentPrefix) {
      const pathParts = currentPrefix.split("/").filter((part) => part);
      pathParts.pop();
      const newPrefix = pathParts.length > 0 ? pathParts.join("/") : "";

      const hasValidMapping = folderMappingsStorage.getFname(newPrefix);

      if (!hasValidMapping && newPrefix !== "") {
        folderMappingsStorage.clearMappingsForHigherLevel("");
        setCurrentPrefix("");
        setCurrentContinuationToken(null);
        setCurrentPage(1);
        setPageTokens([]);
        setSortBy("name_asc");
        setCurrentFolderType("all");
        updateURL({ prefix: "", page: 1, folderType: "all" });
      } else {
        folderMappingsStorage.clearMappingsForHigherLevel(newPrefix);
        setCurrentPrefix(newPrefix);
        setCurrentContinuationToken(null);
        setCurrentPage(1);
        setPageTokens([]);
        updateURL({ prefix: newPrefix, page: 1 });
      }
    } else {
      navigate(-1);
    }
  };

  const buildBreadcrumbs = useCallback(() => {
    const breadcrumbs = [];

    breadcrumbs.push({
      fname: "Home",
      fsource: "",
      isClickable: currentPrefix,
    });

    if (!currentPrefix) {
      return breadcrumbs;
    }

    const pathParts = currentPrefix.split("/").filter((part) => part);

    let currentPath = "";
    for (let i = 0; i < pathParts.length; i++) {
      currentPath += (currentPath ? "/" : "") + pathParts[i];
      const fname = folderMappingsStorage.getFname(currentPath);
      if (!fname) {
        continue;
      }
      const isLast = i === pathParts.length - 1;

      breadcrumbs.push({
        fname,
        fsource: currentPath,
        isClickable: !isLast,
      });
    }

    return breadcrumbs;
  }, [currentPrefix]);

  const handleBreadcrumbClick = (fsource: string) => {
    if (fsource === currentPrefix) {
      return;
    }

    try {
      folderMappingsStorage.clearMappingsForHigherLevel(fsource);

      setCurrentPrefix(fsource);
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
      if (fsource === "") {
        setSortBy("name_asc");
        setCurrentFolderType("all");
        updateURL({ prefix: "", page: 1, folderType: "all" });
      } else {
        updateURL({ prefix: fsource, page: 1 });
      }
    } catch (error) {
      setCurrentPrefix("");
      setCurrentContinuationToken(null);
      setCurrentPage(1);
      setPageTokens([]);
      setSortBy("name_asc");
      setCurrentFolderType("all");
      updateURL({ prefix: "", page: 1, folderType: "all" });
    }
  };

  if (userLoading) {
    return (
      <Layout title="File System" content="File System">
        <Container component="main" maxWidth="lg">
          <div
            style={{
              height: "100vh",
              backgroundColor: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </div>
        </Container>
      </Layout>
    );
  }

  const isFolderSyncing =
    !!currentPrefix && fileSystemData?.sync_status === "syncing";

  return (
    <Layout title="File System" content="File System">
      <Container
        component="main"
        maxWidth="lg"
        sx={{
          pt: 3,
          pb: 0,
          px: { xs: 1.5, sm: 3 },
          height: "calc(100vh - 200px)", // Account for header and footer
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {isFolderSyncing && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.92)",
              borderRadius: 2,
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Data is being prepared…
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please wait, this may take a minute.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mb: 1.5,
            flexShrink: 0,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1, sm: 2 },
          }}
        >
          {showFilter && (
            <FolderTypeFilter
              value={currentFolderType}
              onChange={handleFolderTypeChange}
              disabled={!!currentPrefix}
              disabledTooltip="Filter only available at root level"
              showSftpFilter={fileSystemData?.show_sftp_filter === true}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <FileSearch
              onSearch={handleSearch}
              placeholder="Search files and folders..."
              initialValue={currentSearch ?? ""}
            />
          </Box>
        </Box>

        <Box
          sx={{
            mb: 1.5,
            px: { xs: 1, sm: 2 },
            py: 1,
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
          }}
        >
          {currentPrefix && (
            <IconButton
              onClick={handleGoBack}
              size="small"
              sx={{
                width: 40,
                height: 40,
                backgroundColor: "background.paper",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              "& .MuiBreadcrumbs-ol": {
                flexWrap: "nowrap",
                overflow: "hidden",
              },
              "& .MuiBreadcrumbs-li": {
                maxWidth: "100%",
                overflow: "hidden",
                display: "flex",
              },
            }}
          >
            {(() => {
              const all = buildBreadcrumbs();
              const maxVisible = 3;
              const showCollapsed =
                all.length > maxVisible;
              const crumbs = showCollapsed
                ? [
                    all[0],
                    {
                      fname: "…",
                      fsource: "__ellipsis__",
                      isClickable: false,
                    },
                    ...all.slice(-2),
                  ]
                : all;
              return crumbs.map((crumb) =>
                crumb.fsource === "__ellipsis__" ? (
                  <Typography
                    key="ellipsis"
                    component="span"
                    color="text.secondary"
                    sx={{
                      flexShrink: 0,
                      fontWeight: "normal",
                    }}
                  >
                    …
                  </Typography>
                ) : (
                  <Link
                    key={crumb.fsource}
                    underline="hover"
                    color={
                      crumb.isClickable ? "inherit" : "text.primary"
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      if (crumb.isClickable) {
                        handleBreadcrumbClick(crumb.fsource);
                      }
                    }}
                    sx={{
                      cursor: crumb.isClickable ? "pointer" : "default",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontWeight: crumb.isClickable ? "normal" : "bold",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        color: crumb.isClickable
                          ? "primary.main"
                          : "text.primary",
                      },
                    }}
                  >
                    {crumb.fsource === "" && (
                      <Home fontSize="small" sx={{ flexShrink: 0 }} />
                    )}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                      {crumb.fname}
                    </span>
                  </Link>
                ),
              );
            })()}
          </Breadcrumbs>
          {isDbBacked && (
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                gap: 1,
                height: 40,
                flexBasis: { xs: "100%", sm: "auto" },
                minWidth: 0,
              }}
            >
              {isCalibrateAdmin && currentPrefix && (
                <ToggleButtonGroup
                  value={timeDisplayMode}
                  exclusive
                  onChange={(
                    _event: React.MouseEvent<HTMLElement>,
                    value: TimeDisplayMode | null,
                  ) => {
                    if (value != null) {
                      setTimeDisplayMode(value);
                      updateURL({ timeDisplay: value });
                    }
                  }}
                  size="small"
                  sx={{ height: "100%", "& .MuiToggleButton-root": { py: 0 } }}
                >
                  <ToggleButton value="local" aria-label="Local time">
                    Local
                  </ToggleButton>
                  <ToggleButton value="utc" aria-label="UTC">
                    UTC
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
              <FormControl
                size="small"
                sx={{
                  minWidth: 170,
                  height: "100%",
                  "& .MuiInputBase-root": { height: "100%" },
                }}
              >
                <Select<string>
                  value={sortSelectValue}
                  onChange={(e: SelectChangeEvent<string>) => {
                    const next = String(e.target.value ?? sortSelectValue);
                    handleSortChange(next);
                  }}
                  sx={{ fontSize: "0.85rem", height: "100%" }}
                >
                  {isCalibrateAdmin
                    ? [
                        <MenuItem
                          key="date_desc"
                          value="date_desc"
                          disabled={!fileSystemData?.has_direct_files}
                        >
                          Date (Newest first)
                        </MenuItem>,
                        <MenuItem
                          key="date_asc"
                          value="date_asc"
                          disabled={!fileSystemData?.has_direct_files}
                        >
                          Date (Oldest first)
                        </MenuItem>,
                      ]
                    : null}
                  <MenuItem
                    value="size_desc"
                    disabled={!fileSystemData?.has_direct_files}
                  >
                    Size (Largest first)
                  </MenuItem>
                  <MenuItem
                    value="size_asc"
                    disabled={!fileSystemData?.has_direct_files}
                  >
                    Size (Smallest first)
                  </MenuItem>
                  <MenuItem value="name_asc">Name (A → Z)</MenuItem>
                  <MenuItem value="name_desc">Name (Z → A)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            backgroundColor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
            overflow: "clip",
            flex: 1,
            minHeight: 0,
          }}
        >
          <FileList
            showFileDate={isCalibrateAdmin}
            timeDisplayMode={timeDisplayMode}
            items={(fileSystemData?.folders || []).map((item) => {
              if (item.ftype === "folder") {
                const storedFname = folderMappingsStorage.getFname(
                  item.fsource,
                );
                return {
                  ...item,
                  fname: storedFname || item.fname,
                };
              }
              return item;
            })}
            loading={fileSystemLoading || fileSystemFetching}
            error={fileSystemError ? "Failed to load files" : null}
            currentLimit={currentLimit}
            onLimitChange={handleLimitChange}
            onItemClick={handleItemClick}
            onDownloadClick={handleDownloadClick}
            showDownloadButton={true}
            showPagination={true}
            size="medium"
            variant="default"
            maxHeight="100%"
            currentPage={currentPage}
            totalCount={fileSystemData?.total_count}
            totalPages={
              isDbBacked
                ? fileSystemData.total_pages!
                : fileSystemData?.continuation_token
                  ? pageTokens.length + 1
                  : currentPage
            }
            onPageChange={handlePageChange}
            pageTokens={pageTokens}
            downloadingItems={downloadManager.downloadingItems}
            downloadProgress={downloadManager.downloadProgress}
            hasNextPage={
              isDbBacked
                ? currentPage < (fileSystemData?.total_pages || 1)
                : !!fileSystemData?.continuation_token
            }
          />
        </Box>
      </Container>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ maxWidth: { xs: "calc(100vw - 32px)", sm: "100%" } }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
